import { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided
} from 'react-beautiful-dnd'
import { useAlert } from 'react-alert'

import { ModelsDocumentModel } from '../../../../domain/models'
import {
  deleteDocumentModel,
  fetchModelDocuments,
  resetDocuments,
  saveDocumentsModel,
  updateDocumentModel
} from '../../../../main/usecases'
import { getLocalStorage } from '../../../../infra/storage'

import { formatDate, goToUrl } from '../../../utils'
import { useModalBox, useOnChangeDetect } from '../../../contexts'
import {
  Button,
  SubHeader,
  EditorCard,
  Loading,
  ServicesHeader,
  HelperSidebar
} from '../../../components'
import ExplicationModal from './explication-modal'

import { IconSaveSvg, IconReturnSvg } from '../../../assets/icons/svgComponents'

import * as S from './styles'

const Document = () => {
  const [document, setDocument] = useState<ModelsDocumentModel[]>([])
  const [documentText, setDocumentText] = useState('')
  const [htmlList, setHtmlList] = useState<any>({})
  const alert = useAlert()
  const location: any = useLocation()
  const history = useHistory()
  const { modalBox, modalClose } = useModalBox()
  const { refresh, setRefresh } = useOnChangeDetect()

  const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#E0E0E0' : ''
  })

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    userSelect: 'none',
    padding: 1,
    background: isDragging ? '#addfcb' : '',
    ...draggableStyle
  })

  const reorder = (
    list: ModelsDocumentModel[] | null,
    startIndex: number,
    endIndex: number
  ) => {
    if (list) {
      const result = Array.from(list)
      const [removed] = result.splice(startIndex, 1)
      result.splice(endIndex, 0, removed)

      return result
    }
  }

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) return

    if (document) {
      const items = reorder(
        document,
        result.source.index,
        result.destination.index
      )

      if (items) setDocument(items)
    }
  }

  const onAddNewBlock = () => {
    const order = document && document?.length + 1

    const newDocument = {
      ordem: order || 1,
      modeloAdm: null,
      secao: {
        conclusao: 'DEFAULT',
        secao: '',
        texto: '<p></p>'
      },
      tipoDocumentoPsicologico: {
        cdTipoDocumentoPsicologico: location?.state?.cdTypeToDocument,
        descricao: location?.state?.documentTitle
      },
      tipoAvaliacao: {
        cdTipoAvaliacao: location?.state?.cdTypeToService
        // tipoServico: location?.state?.serviceTitle
      }
    }

    setDocument((prev: any) => [...prev, newDocument])
  }

  const handleOpenDeleteModal = ({ id, position }: any) => {
    modalBox(
      <div className="delete-modal">
        <p className="attention-label">
          ATENÇÃO: Todas as edições serão perdidas
        </p>
        <p>Deseja realmente deletar o bloco?</p>
        <div>
          <Button
            hasredButton
            onClick={() => handleRemoveBlock({ id, position })}
          >
            Sim
          </Button>
          <Button hasSecodaryButton onClick={modalClose}>
            Não
          </Button>
        </div>
      </div>,
      'Deletar Bloco',
      '32%'
    )
  }

  const handleRemoveBlock = async ({ id, position }: any) => {
    try {
      if (id) {
        await deleteDocumentModel({
          id: id
        }).delete()
        alert.show('Teste deletado com sucesso', {
          type: 'success',
          timeout: 1000
        })
        modalClose()
        setRefresh((prev: any) => !prev)
      } else if (position >= 0) {
        setDocument((prev) => prev.filter((_, i) => i !== position))
        modalClose()
      }
    } catch (error) {
      console.error(error)
      alert.show('Erro ao deletar um bloco', {
        type: 'error',
        timeout: 4000
      })
    }
  }

  const handleOpenResetModal = () => {
    modalBox(
      <div className="delete-modal">
        <p className="attention-label">
          ATENÇÃO: Todas as edições serão perdidas
        </p>
        <p>Deseja realmente resetar o documento?</p>
        <div>
          <Button
            hasredButton
            onClick={() =>
              handleResetReport(
                location.state.cdTypeToService || null,
                location.state.cdTypeToDocument || null
              )
            }
          >
            Sim
          </Button>
          <Button hasSecodaryButton onClick={modalClose}>
            Não
          </Button>
        </div>
      </div>,
      'Resetar Documento',
      '32%'
    )
  }

  const handleResetReport = async (
    cdTypeToService: number | null,
    cdTypeToDocument: number | null
  ) => {
    try {
      if (cdTypeToService && cdTypeToDocument) {
        const responseToReset = await resetDocuments({
          cdTipoAvaliacao: cdTypeToService,
          cdTypeToDocument: cdTypeToDocument
        }).load()
        modalClose(true)
        alert.show('Documento resetado com sucesso', {
          type: 'success',
          timeout: 4000
        })
        setDocument(responseToReset)
        setRefresh((prev: any) => !prev)
      }
    } catch (error) {
      console.error(error)
      alert.show('Erro ao resetar um documento', {
        type: 'error',
        timeout: 4000
      })
    }
  }

  useEffect(() => {
    if (getLocalStorage('wexer-instructions-to-models') === null) {
      modalBox(<ExplicationModal />, 'Instruções da página')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (document) {
      const texts = document?.map((item) => item?.secao?.texto)
      setHtmlList(texts)
    }
  }, [document, refresh])

  useEffect(() => {
    ;(async () => {
      try {
        if (!location?.state) goToUrl(history, '/documentos')

        const data = await fetchModelDocuments({
          cdTypeToDocument: location?.state?.cdTypeToDocument,
          cdTipoAvaliacao: location?.state?.cdTypeToService
        }).load()
        setDocument(data)
      } catch (error) {
        console.error(error)
      }
    })()
  }, [
    refresh,
    history,
    location?.state,
    location?.state?.cdTypeToDocument,
    location?.state?.cdTypeToService
  ])

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const body = document?.map((item, index) => {
      return {
        ...(item?.modeloAdm === false && {
          cdModeloDocumento: item?.cdModeloDocumento
        }),
        tipoDocumentoPsicologico: {
          cdTipoDocumentoPsicologico:
            item?.tipoDocumentoPsicologico?.cdTipoDocumentoPsicologico
        },
        tipoAvaliacao: {
          cdTipoAvaliacao: location?.state?.cdTypeToService
        },
        secao: {
          secao: item?.secao?.secao,
          texto: htmlList[index] || documentText,
          conclusao: item?.secao?.conclusao
        },
        ordem: item?.ordem
      }
    })

    try {
      if (
        document &&
        (document[0]?.modeloAdm || document[0]?.modeloAdm === null)
      ) {
        const responseToPost = await saveDocumentsModel().post(body)
        alert.show('Salvo com sucesso!', {
          type: 'success',
          timeout: 4000
        })
        setDocument(responseToPost)
        setRefresh((prev: any) => !prev)
      } else {
        const responseToPut = await updateDocumentModel().update(body)
        alert.show('Atualizado com sucesso!', {
          type: 'success',
          timeout: 4000
        })
        setDocument(responseToPut)
        setRefresh((prev: any) => !prev)
      }
    } catch (error) {
      alert.show('Error ao salvar/atualizar', {
        type: 'error',
        timeout: 4000
      })
      console.error(error)
    }
  }

  if (!document) return <Loading />

  return (
    <S.Wrapper>
      <SubHeader page="Voltar" link={`/documentos`} />
      <S.CardContainer>
        <ServicesHeader
          onClick={onSubmit}
          hasReset
          onReset={handleOpenResetModal}
          title={
            `${location?.state?.documentTitle} | ${location?.state?.serviceTitle}` ||
            ''
          }
        />
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided: DroppableProvided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                {document?.map((item, index) => (
                  <Draggable
                    key={item.ordem}
                    draggableId={item?.ordem?.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        <EditorCard
                          data={item && item?.secao?.texto}
                          setData={setDocumentText}
                          title={item && item?.secao?.secao}
                          hasConclusion
                          hasDeleteButton={
                            !item?.modeloAdm || item.modeloAdm === null
                          }
                          openDeleteModal={() =>
                            handleOpenDeleteModal({
                              id: item.cdModeloDocumento || null,
                              position: index
                            })
                          }
                          onChangeContentToEditor={(html) => {
                            setDocumentText((prev: any) => ({
                              ...prev,
                              [index]: html
                            }))
                            setHtmlList((prev: any) => ({
                              ...prev,
                              [index]: html
                            }))
                          }}
                          document={item}
                          height="248px"
                          customTitle
                          onChangeTitle={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const newArr = [...document]

                            newArr[index].secao.secao = e.target.value

                            setDocument(newArr)
                          }}
                          handleChangeSelectInput={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const newArr = [...document]

                            newArr[index].secao.conclusao = e.target.value

                            setDocument(newArr)
                          }}
                          handleDefaultConclusion={(value: boolean) => {
                            const newArr = [...document]

                            if (value === false) {
                              newArr[index].secao.conclusao = 'DEFAULT'
                            }

                            setDocument(newArr)
                          }}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {document.length ? (
          <S.ButtonsContainer>
            <Button
              initialIcon={<IconSaveSvg />}
              className="button-save"
              onClick={onSubmit}
            >
              Salvar
            </Button>
            <Button
              initialIcon={<IconReturnSvg />}
              hasSecodaryButton
              className="button-return-to-list"
            >
              <Link to={`/documentos`}>Retornar à listagem</Link>
            </Button>

            {document[0]?.dataUpdate && (
              <p>
                Data da última atualização:{' '}
                {formatDate(document[0]?.dataUpdate || '2022-05-05')}
              </p>
            )}
          </S.ButtonsContainer>
        ) : (
          ''
        )}
      </S.CardContainer>
      <S.AddButton>
        <Button hasblueButton onClick={onAddNewBlock}>
          Adicionar novo bloco
        </Button>
      </S.AddButton>
      <HelperSidebar />
    </S.Wrapper>
  )
}
export default Document
